import React, { useState } from 'react'
import {
  Badge,
  Button,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip
} from '@chakra-ui/react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { RentalApplication, RentalApplicationFee } from '@homevest/types/rental-applications'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import EditRentalApplicationFeeModal from './EditRentalApplicationFeeModal'
import { formatMoney } from 'lib/numbers'
import { Link } from 'ui'

const RentalApplicationFeeDetailsInner = ({
  rentalApplicationFees
}: {
  rentalApplicationFees: (RentalApplicationFee & { user_name: string })[]
}) => {
  const renderStatus = (fee: RentalApplicationFee & { user_name: string }) => {
    const status =
      fee.stripe_payment_intent_status === 'succeeded' ? (
        <Badge colorScheme='green'>Paid</Badge>
      ) : fee.stripe_payment_intent_status === 'initiated' ? (
        <Badge colorScheme='green'>Initiated</Badge>
      ) : fee.stripe_payment_intent_status === 'processing' ? (
        <Badge colorScheme='yellow'>Processing</Badge>
      ) : fee.stripe_payment_intent_status === 'canceled' ? (
        <Badge colorScheme='red'>Canceled</Badge>
      ) : fee.stripe_payment_intent_status?.startsWith('Failed') ? (
        <HStack spacing={1}>
          <Badge colorScheme='red'>{fee.stripe_payment_intent_status.split('(')[0]}</Badge>
          <Tooltip
            hasArrow
            label={(() => {
              const pattern = /^Failed \(([^)]+)\): (.+)$/
              const match = fee.stripe_payment_intent_status.match(pattern)

              if (match) {
                const [_, statusCode, explanation] = match
                return `${statusCode}: ${explanation}`
              }
              return fee.stripe_payment_intent_status
            })()}
            placement='top'
          >
            <span style={{ cursor: 'help' }}>
              <InformationCircleIcon height={16} />
            </span>
          </Tooltip>
        </HStack>
      ) : (
        <Badge colorScheme='red'>{fee.stripe_payment_intent_status}</Badge>
      )

    return fee.stripe_payment_intent_id ? (
      <Link
        href={`https://dashboard.stripe.com/payments/${fee.stripe_payment_intent_id}`}
        isExternal
        showIcon
      >
        {status}
      </Link>
    ) : (
      status
    )
  }

  return (
    <div>
      {rentalApplicationFees.length > 0 ? (
        <TableContainer>
          <Table variant='simple'>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Amount Expected</Th>
                <Th>Status</Th>
                <Th>Amount Paid</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rentalApplicationFees
                .sort((a, b) => {
                  const nameA = `${a.user_name}`.toLowerCase()
                  const nameB = `${b.user_name}`.toLowerCase()
                  return nameA.localeCompare(nameB)
                })
                .map((fee) => (
                  <Tr key={fee.user_id}>
                    <Td>{fee.user_name}</Td>
                    <Td>{formatMoney(fee.expected_amount, 2, '$')}</Td>
                    <Td>{renderStatus(fee)}</Td>
                    <Td>{formatMoney(fee.amount_paid, 2, '$')}</Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <div>No rental application fees!</div>
      )}
    </div>
  )
}

const RentalApplicationFeeDetails = ({
  rentalApplication,
  onRefresh
}: {
  rentalApplication: RentalApplication
  onRefresh?: () => void | Promise<void>
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const leadGroupUsers = rentalApplication.lead_group.lead_group_users
  const rentalApplicationFees = leadGroupUsers.flatMap((lgu) => {
    const fee = lgu.users.recent_rental_application_fee
    return fee
      ? [
          {
            ...fee,
            user_name: `${lgu.users.first_name} ${lgu.users.last_name}`
          }
        ]
      : []
  })

  return (
    <>
      <ContentSectionCard
        title='Rental Application Fees'
        collapsable={rentalApplicationFees.length > 0}
        isOpenDefault={rentalApplicationFees.length > 0}
        padding
        action={
          <Button
            size='xs'
            colorScheme={'teal'}
            onClick={(e) => {
              e.stopPropagation()
              setIsModalOpen(true)
            }}
          >
            Edit
          </Button>
        }
      >
        <RentalApplicationFeeDetailsInner rentalApplicationFees={rentalApplicationFees} />
      </ContentSectionCard>
      {isModalOpen && (
        <EditRentalApplicationFeeModal
          isOpen={isModalOpen}
          close={() => setIsModalOpen(false)}
          rentalApplicationFees={rentalApplicationFees}
          onRefresh={onRefresh}
        />
      )}
    </>
  )
}

export default RentalApplicationFeeDetails
